/**
 * Typed API client for the event-website-server.
 */

export const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

// ---------------------------------------------------------------------------
// Types — mirrors server response shapes
// ---------------------------------------------------------------------------

export interface StartGenerationResponse {
  runId: string;
}

export interface PipelineStatus {
  runId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  currentStep: string | null;
  steps: Array<{
    step: string;
    success: boolean;
    durationMs: number;
    error: string | null;
  }>;
  fixAttempts: number;
  startedAt: string;
  completedAt: string | null;
}

export interface PipelineOutput {
  html: string;
  contentJson: string;
  outputDir?: string;
}

export interface SSEEvent {
  type: string;
  data: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/** Starts a new website generation pipeline run. */
export async function startGeneration(eventJson: Record<string, unknown>): Promise<StartGenerationResponse> {
  const res = await fetch(`${API_BASE}/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eventJson }),
  });
  if (!res.ok) throw new Error(`Failed to start generation: ${res.statusText}`);
  return res.json();
}

/** Returns the current status of a pipeline run. */
export async function getStatus(runId: string): Promise<PipelineStatus> {
  const res = await fetch(`${API_BASE}/generate/${runId}/status`);
  if (!res.ok) throw new Error(`Failed to get status: ${res.statusText}`);
  return res.json();
}

export interface HistoryRun {
  folderName: string;
  timestamp: string | null;
  runId: string | null;
  fixAttempts: number;
}

/** Lists all previous pipeline runs. */
export async function getHistory(): Promise<HistoryRun[]> {
  const res = await fetch(`${API_BASE}/generate/history`);
  if (!res.ok) throw new Error(`Failed to get history: ${res.statusText}`);
  const data = await res.json();
  return data.runs;
}

/** Returns the output for a historical run by folder name. */
export async function getHistoryOutput(folderName: string): Promise<PipelineOutput> {
  const res = await fetch(`${API_BASE}/generate/history/${encodeURIComponent(folderName)}`);
  if (!res.ok) throw new Error(`Failed to get history output: ${res.statusText}`);
  return res.json();
}

export interface PublishResult {
  url: string;
  bucket: string;
  key: string;
}

/** Publishes a generated site to S3 for sharing. */
export async function publishSite(folderName: string): Promise<PublishResult> {
  const res = await fetch(`${API_BASE}/generate/history/${encodeURIComponent(folderName)}/publish`, {
    method: 'POST',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Failed to publish: ${res.statusText}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Templates & Editing
// ---------------------------------------------------------------------------

export interface TemplateInfo {
  id: string;
  name: string;
  description: string | null;
}

/** Lists available templates. */
export async function getTemplates(): Promise<TemplateInfo[]> {
  const res = await fetch(`${API_BASE}/generate/templates`);
  if (!res.ok) throw new Error(`Failed to get templates: ${res.statusText}`);
  const data = await res.json();
  return data.templates;
}

/** Starts a pipeline run from an existing template with new event data. */
export async function startFromTemplate(
  templateId: string,
  eventJson: Record<string, unknown>,
): Promise<StartGenerationResponse> {
  const res = await fetch(`${API_BASE}/generate/from-template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ templateId, eventJson }),
  });
  if (!res.ok) throw new Error(`Failed to start from template: ${res.statusText}`);
  return res.json();
}

/** Starts a personalized template pipeline — adapts the template's design to match the event. */
export async function startFromTemplatePersonalized(
  templateId: string,
  eventJson: Record<string, unknown>,
): Promise<StartGenerationResponse> {
  const res = await fetch(`${API_BASE}/generate/from-template-personalized`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ templateId, eventJson }),
  });
  if (!res.ok) throw new Error(`Failed to start personalized template: ${res.statusText}`);
  return res.json();
}

/** Saves a generated site as a reusable template. */
export async function saveAsTemplate(
  folderName: string,
  options?: { name?: string; description?: string },
): Promise<{ id: string; name: string }> {
  const res = await fetch(`${API_BASE}/generate/history/${encodeURIComponent(folderName)}/save-template`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: options?.name, description: options?.description }),
  });
  if (!res.ok) throw new Error(`Failed to save template: ${res.statusText}`);
  return res.json();
}

/** Renames an existing template. */
export async function renameTemplate(templateId: string, name: string): Promise<void> {
  const res = await fetch(`${API_BASE}/generate/templates/${encodeURIComponent(templateId)}/rename`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`Failed to rename template: ${res.statusText}`);
}

/** Starts an edit pipeline to apply presentational changes to an existing site. */
export async function startEdit(folderName: string, instruction: string): Promise<StartGenerationResponse> {
  const res = await fetch(`${API_BASE}/generate/edit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ folderName, instruction }),
  });
  if (!res.ok) throw new Error(`Failed to start edit: ${res.statusText}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Content transformation
// ---------------------------------------------------------------------------

/** Transforms raw EventJSON into WebsiteContent JSON (for content swapping). */
export async function transformEventJson(eventJson: Record<string, unknown>): Promise<string> {
  const res = await fetch(`${API_BASE}/generate/transform`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eventJson }),
  });
  if (!res.ok) throw new Error(`Failed to transform: ${res.statusText}`);
  const data = await res.json();
  return data.contentJson;
}

/** Returns the generated output of a completed run. */
export async function getOutput(runId: string): Promise<PipelineOutput> {
  const res = await fetch(`${API_BASE}/generate/${runId}/output`);
  if (!res.ok) throw new Error(`Failed to get output: ${res.statusText}`);
  return res.json();
}

/** Subscribes to pipeline SSE events. Returns a cleanup function. */
export function subscribeToEvents(
  runId: string,
  onEvent: (event: SSEEvent) => void,
  onDone: () => void,
  onError: (error: Error) => void,
): () => void {
  const eventSource = new EventSource(`${API_BASE}/generate/${runId}/events`);

  eventSource.onmessage = e => {
    try {
      const data = JSON.parse(e.data);
      onEvent({ type: e.type || 'message', data });
    } catch {
      // ignore malformed events
    }
  };

  eventSource.addEventListener('step-start', e => {
    onEvent({ type: 'step-start', data: JSON.parse((e as MessageEvent).data) });
  });

  eventSource.addEventListener('step-complete', e => {
    onEvent({ type: 'step-complete', data: JSON.parse((e as MessageEvent).data) });
  });

  eventSource.addEventListener('step-error', e => {
    onEvent({ type: 'step-error', data: JSON.parse((e as MessageEvent).data) });
  });

  eventSource.addEventListener('pipeline-complete', e => {
    onEvent({ type: 'pipeline-complete', data: JSON.parse((e as MessageEvent).data) });
    eventSource.close();
    onDone();
  });

  eventSource.addEventListener('pipeline-error', e => {
    onEvent({ type: 'pipeline-error', data: JSON.parse((e as MessageEvent).data) });
    eventSource.close();
    onError(new Error('Pipeline failed'));
  });

  eventSource.onerror = () => {
    eventSource.close();
    onError(new Error('SSE connection lost'));
  };

  return () => eventSource.close();
}
